<template>
  <div class="goal-container">
    <span v-if="!isEditing">
      Уважаемый пользователь {{ user.name }}, для заработка суммы в размере 
      <span 
        class="font-weight-bold goal-value" 
        @click="startEditing"
      >
        {{ formatMoney(displayedGoal) }}
      </span>
    </span>
    
    <div v-else class="goal-edit-container">
      Уважаемый пользователь {{ user.name }}, для заработка суммы в размере 
      <div class="d-inline-flex align-center">
        <input 
          ref="goalInput"
          v-model="editGoalValue" 
          type="number" 
          min="0" 
          step="1" 
          class="goal-input"
          @keydown.enter="saveGoal"
        />
        <v-btn 
          icon 
          small 
          color="success" 
          class="ml-2"
          @click="saveGoal"
        >
          <v-icon>mdi-check</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IncomeGoalEditor',
  
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  
  data() {
    return {
      isEditing: false,
      editGoalValue: 0,
      personalGoal: null,
      isLoading: false
    };
  },
  
  computed: {
    // Определяем, какую цель показывать
    displayedGoal() {
      // Приоритет 1: Персональная цель
      if (this.personalGoal && this.personalGoal > 0) {
        return this.personalGoal;
      }
      
      // Приоритет 2: Цель пользователя из профиля
      if (this.user?.income_goal && this.user.income_goal > 0) {
        return this.user.income_goal;
      }
      
      // Приоритет 3: Значение по умолчанию
      return 1200000;
    }
  },
  
  watch: {
    // Отслеживаем изменение displayedGoal для обновления в родительском компоненте
    displayedGoal: {
      immediate: true,
      handler(newValue) {
        // Эмитим событие для обновления в родительском компоненте
        this.$emit('update:goal-value', newValue);
      }
    }
  },
  
  async mounted() {
    // Загружаем персональную цель при монтировании компонента
    await this.loadPersonalGoal();
    
    // Добавляем обработчик клика вне компонента с небольшой задержкой
    this.$nextTick(() => {
      document.addEventListener('click', this.handleClickOutside);
    });
  },
  
  beforeDestroy() {
    // Удаляем обработчик при уничтожении компонента
    document.removeEventListener('click', this.handleClickOutside);
  },
  
  methods: {
       // Загрузка персональной цели с сервера
       async loadPersonalGoal() {
      try {
        this.isLoading = true;
        const response = await this.axios.get('user/get-goal-self');
        
        if (response.data.success && response.data.data) {
          this.personalGoal = response.data.data.income_goal_self;
          // Эмитим событие для обновления в родительском компоненте
          this.$emit('update:goal-value', this.displayedGoal);
        }
      } catch (error) {
        console.error('Error loading personal goal:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    // Сохранение персональной цели
    async setGoalSelf(goalValue) {
      try {
        this.isLoading = true;
        await this.axios.post('user/set-goal-self', {
          income_goal_self: goalValue
        });
        
        // Обновляем локальное значение после успешного сохранения
        this.personalGoal = goalValue;
        
        // Эмитим событие для обновления в родительском компоненте
        this.$emit('update:goal-value', goalValue);
      } catch (error) {
        console.error('Error updating income goal:', error);
        // Можно добавить уведомление пользователя об ошибке
        this.$toast?.error('Не удалось сохранить цель по доходу');
      } finally {
        this.isLoading = false;
      }
    },
    
    // Форматирование денежной суммы
    formatMoney(value) {
      if (!value && value !== 0) return '';
      
      // Форматируем число с разделителями тысяч
      return new Intl.NumberFormat('ru-RU').format(value);
    },
    
    // Начало редактирования
    startEditing(event) {
      if (this.isLoading) return;
      
      // Предотвращаем всплытие события, чтобы не сработал handleClickOutside
      if (event) {
        event.stopPropagation();
      }
      
      this.isEditing = true;
      this.editGoalValue = this.displayedGoal;
      
      // Фокусируемся на поле ввода после его отображения
      this.$nextTick(() => {
        this.$refs.goalInput.focus();
        this.$refs.goalInput.select(); // Выделяем текущее значение для удобства
      });
    },
    
    // Сохранение цели
    async saveGoal() {
      if (this.isLoading) return;
      
      // Проверяем, что введено корректное значение
      const goalValue = parseInt(this.editGoalValue);
      
      if (isNaN(goalValue) || goalValue < 0) {
        this.$toast?.warning('Пожалуйста, введите корректное положительное число');
        return;
      }
      
      // Сохраняем новое значение
      await this.setGoalSelf(goalValue);
      
      // Выходим из режима редактирования
      this.isEditing = false;
    },
    
    // Обработчик клика вне компонента
    handleClickOutside(event) {
      // Если мы в режиме редактирования и клик был не на элементах редактирования
      if (this.isEditing && 
          this.$refs.goalInput && 
          !this.$refs.goalInput.contains(event.target) && 
          !event.target.closest('.goal-edit-container') &&
          !event.target.classList.contains('goal-value')) { // Добавлена проверка на клик по элементу с классом goal-value
        
        // Сохраняем текущее значение
        this.saveGoal();
      }
    },
    
    // Отмена редактирования (можно добавить кнопку отмены, если нужно)
    cancelEditing() {
      this.isEditing = false;
      this.editGoalValue = this.displayedGoal;
    }
  }
}
</script>

<style scoped>
.goal-container {
  position: relative;
}

.goal-value {
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.goal-value:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.goal-input {
  max-width: 150px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-weight: bold;
  text-align: right;
}

/* Убираем стрелки для числового input в разных браузерах */
.goal-input::-webkit-outer-spin-button,
.goal-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.goal-input[type=number] {
  -moz-appearance: textfield; /* Firefox */
}

.goal-edit-container {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
}
</style>